import React, { useState, useEffect } from 'react';
import './WebhookModal.css';

const WebhookModal = ({ isOpen, onClose, onSave, webhook = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    verifyToken: '',
    subscribedFields: [],
    wabaId: ''
  });
  const [errors, setErrors] = useState({});

  const availableFields = [
    'messages',
    'message_status',
    'message_template_status',
    'phone_number_name_update',
    'phone_number_quality_update',
    'account_review_update',
    'account_update',
    'business_verification_status'
  ];

  const isEditMode = !!webhook;

  useEffect(() => {
    if (webhook) {
      setFormData({
        name: webhook.name || '',
        url: webhook.url || '',
        verifyToken: webhook.verifyToken || '',
        subscribedFields: webhook.subscribedFields || [],
        wabaId: webhook.wabaId || ''
      });
    } else {
      setFormData({
        name: '',
        url: '',
        verifyToken: '',
        subscribedFields: ['messages'],
        wabaId: ''
      });
    }
    setErrors({});
  }, [webhook, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFieldToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      subscribedFields: prev.subscribedFields.includes(field)
        ? prev.subscribedFields.filter(f => f !== field)
        : [...prev.subscribedFields, field]
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Webhook name is required';
    }

    if (!formData.url.trim()) {
      newErrors.url = 'Webhook URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.url)) {
      newErrors.url = 'URL must start with http:// or https://';
    }

    if (!formData.verifyToken.trim()) {
      newErrors.verifyToken = 'Verify token is required';
    }

    if (formData.subscribedFields.length === 0) {
      newErrors.subscribedFields = 'At least one field must be subscribed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onClose();
    }
  };

  const generateToken = () => {
    const token = Math.random().toString(36).substring(2, 15) + 
                  Math.random().toString(36).substring(2, 15);
    setFormData(prev => ({ ...prev, verifyToken: token }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content webhook-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Webhook' : 'Create Webhook'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="name">Webhook Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Production Webhook"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="url">Webhook URL *</label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="https://api.example.com/webhook"
              className={errors.url ? 'error' : ''}
            />
            {errors.url && <span className="error-message">{errors.url}</span>}
            <span className="help-text">Your server endpoint that will receive webhook events. Must be HTTPS.</span>
          </div>

          <div className="form-group">
            <label htmlFor="verifyToken">Verify Token *</label>
            <div className="token-input-group">
              <input
                type="text"
                id="verifyToken"
                name="verifyToken"
                value={formData.verifyToken}
                onChange={handleChange}
                placeholder="Your verify token"
                className={errors.verifyToken ? 'error' : ''}
              />
              <button type="button" className="btn-generate" onClick={generateToken}>
                Generate
              </button>
            </div>
            {errors.verifyToken && <span className="error-message">{errors.verifyToken}</span>}
            <span className="help-text">Token used to verify webhook requests from WhatsApp</span>
          </div>

          <div className="form-group">
            <label htmlFor="wabaId">WhatsApp Business Account ID (WABA ID)</label>
            <input
              type="text"
              id="wabaId"
              name="wabaId"
              value={formData.wabaId}
              onChange={handleChange}
              placeholder="123456789012345"
            />
            <span className="help-text">Optional: Specific WABA ID to subscribe to</span>
          </div>

          <div className="form-group">
            <label>Subscribed Fields *</label>
            {errors.subscribedFields && (
              <span className="error-message">{errors.subscribedFields}</span>
            )}
            <div className="fields-grid">
              {availableFields.map(field => (
                <label key={field} className="field-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.subscribedFields.includes(field)}
                    onChange={() => handleFieldToggle(field)}
                  />
                  <span className="field-label">{field}</span>
                </label>
              ))}
            </div>
            <span className="help-text">Select which events you want to receive</span>
          </div>

          {isEditMode && (
            <div className="info-section">
              <h3>Webhook Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className="info-value">{webhook.status || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Total Events:</span>
                  <span className="info-value">{webhook.totalEvents || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Success Rate:</span>
                  <span className="info-value">{webhook.successRate?.toFixed(2) || '0.00'}%</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created At:</span>
                  <span className="info-value">
                    {webhook.createdAt 
                      ? new Date(webhook.createdAt).toLocaleString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              {isEditMode ? 'Update Webhook' : 'Create Webhook'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebhookModal;

